import { ComponentRef, Injectable, Type } from '@angular/core';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import { AppService } from '@app/app.service';
import { enumHtsChatScope } from '@data-layer/enums/common';
import { EntrySummaryModel, LineModel } from '@data-layer/models/entry-summaries';
import { EntrySummaryStateService } from '@shared/es-mutual/services';
import { HtsAssistantDrawerComponent } from '../components/hts-assistant-drawer/hts-assistant-drawer.component';
import { HTS_CATALOG_WINDOW_Z_INDEX, HTS_LINE_PANEL_Z_INDEX } from '../const/hts-assistant.const';
import { enumHtsAssistantMode } from '../enums';
import { HtsLineStatusHelper } from '../helpers/hts-line-status.helper';
import { IHtsAssistantCatalogLaunchConfig, IHtsAssistantLaunchConfig, IHtsAssistantLineRef } from '../interfaces';

/**
 * The one place that opens the standalone assistant drawer (from "Find HTS", and on the BINA Catalog page — #13554). Same
 * shell as the BINA classifier: the as-window sidebar layer, medium width. An Insert (`lineUpdated`) closes the drawer once
 * the entry is re-read, so the host shows the saved line with its code (#13529); the drawer also follows the host form when
 * the form is destroyed.
 */
@Injectable({ providedIn: 'root' })
export class HtsAssistantLauncherService {
  /** The es-mutual line panel (registered by its module: the launcher cannot import it without a module cycle). */
  private linePanelComponent: Type<unknown> | null = null;

  constructor(private appService: AppService, private entrySummaryStateService: EntrySummaryStateService) {}

  public registerLinePanel(component: Type<unknown>): void {
    this.linePanelComponent = component;
  }

  public open(config: IHtsAssistantLaunchConfig): ComponentRef<HtsAssistantDrawerComponent> {
    this.appService.isXlg$.next(true);
    // the line details behind the eye button (demo rework 2026-10-16): a saved line in live mode, where the host asked for it
    const isLinePanel: boolean =
      config.isLinePanel === true && config.mode === enumHtsAssistantMode.Live && config.lineId !== null && !!this.linePanelComponent;

    const ref: ComponentRef<HtsAssistantDrawerComponent> = this.appService.openSidebarAsWindow(HtsAssistantDrawerComponent, {
      sidebarHeaderText: '',
      isMd: true,
      isFillMobile: true,
      customZIndex: config.customZIndex || (isLinePanel ? HTS_LINE_PANEL_Z_INDEX : undefined),
    });
    const destroyed$: Subject<void> = new Subject<void>();
    let isDestroyed: boolean = false;
    ref.onDestroy(() => {
      isDestroyed = true;
      destroyed$.next();
      destroyed$.complete();
    });

    const drawer: HtsAssistantDrawerComponent = ref.instance;
    drawer.mode = config.mode;
    drawer.scope = config.scope;
    drawer.lineId = config.lineId;
    drawer.invoiceId = config.invoiceId;
    drawer.companyId = config.companyId;
    drawer.lineDescription = config.lineDescription;
    drawer.currentHts = config.currentHts;
    if (isLinePanel) {
      drawer.panelComponent = this.linePanelComponent;
      drawer.isPanelNavigation = false;
    }
    this.applyVerification(drawer, config.lineId, this.entrySummaryStateService.entrySummary$.getValue());

    drawer.close.pipe(takeUntil(destroyed$)).subscribe(() => this.close(ref));
    // The form does not carry the verification: it is read from the entry in the store, and followed after every reload
    // (an insert verifies the line, a description save clears the verification).
    this.entrySummaryStateService.entrySummary$
      .pipe(
        filter((entry: EntrySummaryModel) => !!entry),
        takeUntil(destroyed$),
      )
      .subscribe((entry: EntrySummaryModel) => this.applyVerification(drawer, config.lineId, entry));
    // Insert on a saved line updated it on the backend: refresh the entry so line lists and statuses follow (#13383), then
    // close (#13529) — the host sees the fresh store by the time the drawer is gone.
    // (the store refresh is wanted even when the user closed the drawer meanwhile; the close then has nothing to do)
    drawer.lineUpdated.pipe(takeUntil(destroyed$)).subscribe(() =>
      this.reloadEntry(() => {
        if (!isDestroyed) {
          this.close(ref);
        }
      }),
    );

    if (config.hostDestroy$) {
      // the drawer belongs to the host form: when the form goes away, so does the drawer
      config.hostDestroy$.pipe(take(1), takeUntil(destroyed$)).subscribe(() => this.close(ref));
    }
    if (config.draftDescription$) {
      config.draftDescription$
        .pipe(takeUntil(destroyed$))
        .subscribe((description: string | null) => drawer.setDraftDescription(description));
    }

    return ref;
  }

  /**
   * The assistant on the BINA Catalog page (#13554): no line, the conversation belongs to the company. The page's window level
   * without a backdrop (the catalog list stays in view and refreshes behind the drawer on a save), filled on mobile like every
   * drawer.
   */
  public openForCatalog(config: IHtsAssistantCatalogLaunchConfig): ComponentRef<HtsAssistantDrawerComponent> {
    this.appService.isXlg$.next(true);
    const ref: ComponentRef<HtsAssistantDrawerComponent> = this.appService.openSidebarAsWindow(HtsAssistantDrawerComponent, {
      sidebarHeaderText: '',
      isMd: true,
      isFillMobile: true,
      hideBackDrop: true,
      customZIndex: HTS_CATALOG_WINDOW_Z_INDEX,
    });
    const drawer: HtsAssistantDrawerComponent = ref.instance;
    drawer.mode = enumHtsAssistantMode.Live;
    drawer.scope = enumHtsChatScope.Importer;
    drawer.lineId = null;
    drawer.companyId = config.companyId;
    drawer.isCatalogMode = true;
    drawer.initialComposerText = config.initialDescription;
    drawer.close.pipe(take(1)).subscribe(() => this.close(ref));
    return ref;
  }

  private applyVerification(drawer: HtsAssistantDrawerComponent, lineId: number | null, entry: EntrySummaryModel | null): void {
    const line: LineModel | null = lineId !== null ? HtsLineStatusHelper.findLineModel(entry, lineId) : null;
    // verification first, then the saved description: a description saved from the page form starts the chat for the new text
    drawer.setVerification(
      line ? line.htsVerificationSource : null,
      line ? line.htsVerificationMatchedText : null,
      line ? HtsLineStatusHelper.getReason(line) : null,
    );
    if (line) {
      drawer.setLineDescription(line.description);
    }
    // the panel resolves the line through its ref (shipment / invoice / line ids, status): kept current with the entry
    if (drawer.panelComponent) {
      const lines: IHtsAssistantLineRef[] = entry ? HtsLineStatusHelper.flattenLines(entry) : [];
      drawer.setPanelLineRef(lines.find((ref: IHtsAssistantLineRef) => ref.lineId === lineId) || null);
    }
  }

  /** `done` runs once the store is refreshed — also when the read fails or there is nothing to read. */
  private reloadEntry(done: () => void): void {
    const entry: EntrySummaryModel = this.entrySummaryStateService.entrySummary$.getValue();
    if (!entry || !entry.referenceNumber) {
      done();
      return;
    }
    this.entrySummaryStateService
      .loadEntrySummary(entry.referenceNumber)
      .pipe(take(1))
      .subscribe(
        (loaded: EntrySummaryModel) => {
          this.entrySummaryStateService.updateCurrentEntrySummary(loaded);
          done();
        },
        () => done(),
      );
  }

  private close(ref: ComponentRef<HtsAssistantDrawerComponent>): void {
    if (this.appService.componentSidebarAsWindowRef !== ref) {
      return; // another window replaced this drawer meanwhile
    }
    this.appService.closeSidebarAsWindow();
    this.appService.isXlg$.next(false);
  }
}
